// src/components/pages/Movimento/profiles/getMovimentoProfile.js

import { entradaProfile } from './entrada/index';
import { saidaProfile } from './saida/index';
import { operacaoOverrides } from './operacoes';
import { mergeTabsNormalized, mergeUiTabs } from '../../../../helpers/helpers';

/**
 * Escolhe o profile base conforme a natureza.
 */
const pickBaseByNatureza = (natureza) => {
    const n = String(natureza || '')?.toLowerCase();

    if (n === 'entrada') return entradaProfile;
    if (n === 'saida') return saidaProfile;

    // fallback seguro
    return entradaProfile;
};

/**
 * Quando a operação não informa natureza explicitamente na URL,
 * tenta inferir pelas flags da operação.
 */
const inferNaturezaByOperacaoFlags = (operacao) => {
    if (operacao?.entrada && !operacao?.saida) return 'Entrada';
    if (operacao?.saida && !operacao?.entrada) return 'Saida';

    // operações ambíguas, ex: CFR
    return null;
};

/**
 * Normaliza chaves legadas do override para o formato canônico.
 *
 * Problemas tratados:
 * - override.uitabs  -> override.uiTabs
 * - override.itens   -> override.parametros.itens
 *
 * Assim o restante do sistema pode consumir:
 * - profile.uiTabs.*
 * - profile.parametros.*
 */
const normalizeOverride = (overrideRaw = {}) => {
    const uiTabsCompat = overrideRaw.uiTabs || overrideRaw.uitabs || {};

    return {
        ...overrideRaw,

        // força o nome canônico
        uiTabs: uiTabsCompat,

        // promove "itens" legado para "parametros.itens"
        parametros: {
            ...(overrideRaw.parametros || {}),
            itens: {
                ...(overrideRaw.parametros?.itens || {}),
            },
        },
    };
};

export const getMovimentoProfile = ({ operacao, naturezaUrl, operacaoParametros }) => {
    const sigla = operacao?.sigla;

    const overrideRaw = (sigla && operacaoOverrides[sigla]) || {};
    const override = normalizeOverride(overrideRaw);

    /**
     * 1) Resolve natureza
     * Prioridade:
     * - URL
     * - override
     * - inferência por flags da operação
     * - fallback Entrada
     */
    const naturezaInferida = naturezaUrl || override.natureza || inferNaturezaByOperacaoFlags(operacao) || 'Entrada';

    /**
     * 2) Base por natureza
     */
    const base = pickBaseByNatureza(naturezaInferida);

    /**
     * 3) Tabs (enabled/disabled)
     */
    const tabsFinal = mergeTabsNormalized(base.tabs || {}, override.tabs || {});

    /**
     * 4) uiTabs (parte visual)
     */
    const uiTabsFinal = mergeUiTabs(base.uiTabs || {}, override.uiTabs || {});

    /**
     * ------------------------------------------------------------
     * COMPATIBILIDADE LEGADA
     * ------------------------------------------------------------
     * Se ainda existir:
     * - base/override.cabecalho
     * - base/override.labels
     *
     * injeta em uiTabs.cabecalho e uiTabs.cabecalho.labels
     */
    const legacyCabecalho = {
        ...(base.cabecalho || {}),
        ...(override.cabecalho || {}),
    };

    const legacyLabels = {
        ...(base.labels || {}),
        ...(override.labels || {}),
    };

    const uiTabsCabecalhoCompat = {
        ...(uiTabsFinal.cabecalho || {}),
        ...legacyCabecalho,
        labels: {
            ...((uiTabsFinal.cabecalho || {}).labels || {}),
            ...legacyLabels,
        },
    };

    const uiTabsFinalCompat = {
        ...uiTabsFinal,
        cabecalho: uiTabsCabecalhoCompat,
    };

    /**
     * 5) labelsByNatureza
     * Para operações ambíguas como CFR.
     * Aplicado por último para ter prioridade final.
     */
    const overrideUiTabs = override.uiTabs || {};
    const labelsByNatureza = overrideUiTabs?.cabecalho?.labelsByNatureza;

    const labelsNatureza =
        labelsByNatureza?.[naturezaInferida] || labelsByNatureza?.[String(naturezaInferida)?.toLowerCase()] || null;

    if (labelsNatureza) {
        uiTabsFinalCompat.cabecalho = uiTabsFinalCompat.cabecalho || {};
        uiTabsFinalCompat.cabecalho.labels = uiTabsFinalCompat.cabecalho.labels || {};
        uiTabsFinalCompat.cabecalho.labels = {
            ...uiTabsFinalCompat.cabecalho.labels,
            ...labelsNatureza,
        };
    }

    /**
     * 6) parâmetros canônicos
     *
     * Faz merge completo do topo e merge profundo em itens,
     * porque o módulo de itens lê principalmente:
     * profile.parametros.itens.*
     */
    const parametrosFinal = {
        ...(base.parametros || {}),
        ...(override.parametros || {}),
        itens: {
            ...(base.parametros?.itens || {}),
            ...(override.parametros?.itens || {}),
        },
    };

    /**
     * 7) extras / compat
     */
    const parceiroVai = override.parceiroVai ?? base.parceiroVai;

    const mostraEndereco = override.mostraEndereco ?? base.mostraEndereco ?? true;
    const enderecoPrefixoUI = mostraEndereco ? (override.enderecoPrefixoUI ?? base.enderecoPrefixoUI) : null;

    /**
     * 8) tabela preço
     *
     * Se vier explicitamente de operacaoParametros.formTabelaOrProduto,
     * isso tem prioridade.
     *
     * Senão, tenta override/base e por último parametros.itens.usaTabelaPreco
     */
    const usaTabelaPrecoByParam = operacaoParametros?.formTabelaOrProduto;

    const usaTabelaPrecoFinal =
        typeof usaTabelaPrecoByParam === 'boolean'
            ? usaTabelaPrecoByParam
            : (override.usaTabelaPreco ?? base.usaTabelaPreco ?? parametrosFinal?.itens?.usaTabelaPreco);

    /**
     * 9) retorno final
     */
    return {
        ...base,
        ...override,

        natureza: naturezaInferida,

        // canônicos
        tabs: tabsFinal,
        uiTabs: uiTabsFinalCompat,
        parametros: parametrosFinal,

        // aliases para telas antigas
        cabecalho: uiTabsFinalCompat.cabecalho,
        labels: uiTabsFinalCompat.cabecalho?.labels,

        // extras
        parceiroVai,
        enderecoPrefixoUI,

        // compat tabela preço
        formTabelaOrProduto: usaTabelaPrecoByParam,
        usaTabelaPreco: usaTabelaPrecoFinal,
    };
};
